// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CartPageTemplate, { CartPageTemplateStyles } from "@conklin/App/Components/Cart/CartPageTemplate";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import Zone from "@insite/client-framework/Components/Zone";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import React from "react";
import { css } from "styled-components";

type Props = WidgetProps;

export interface OnePageCheckoutPageContainerStyles {
    container?: GridContainerProps;
    leftGridItem?: GridItemProps;
    spaceGridItem?: GridItemProps;
    rightGridItem?: GridItemProps;
    // XNG-Changes
    backToCartLink?: LinkPresentationProps;
    cartPageTemplate?: CartPageTemplateStyles;
}

export const containerStyles: OnePageCheckoutPageContainerStyles = {
    leftGridItem: {
        width: [12, 12, 6, 7, 7],
        css: css`
            & > div > *:not(:last-child) {
                padding-bottom: 30px;
                margin-bottom: 30px;
                border-bottom: 1px solid ${getColor("common.border")};
            }
        `,
    },
    spaceGridItem: { width: [0, 0, 1, 1, 1] },
    rightGridItem: {
        width: [12, 12, 5, 4, 4],
        css: css`
            & > div > *:not(:last-child) {
                margin-bottom: 10px;
            }
        `,
    },
};

const styles = containerStyles;

const OnePageCheckoutPageContainer = ({ id }: Props) => {
    const cartPageUrl = useAppSelector(state => getPageLinkByPageType(state, "CartPage"))?.url;
    return (
        <CartPageTemplate extendedStyles={styles.cartPageTemplate}>
            <CartPageTemplate.HeaderSection>
                <Link {...styles.backToCartLink} href={cartPageUrl}>
                    {translate("Return to Cart")}
                </Link>
            </CartPageTemplate.HeaderSection>
            <CartPageTemplate.MainSection>
                <Zone zoneName="Content0" contentId={id} />
            </CartPageTemplate.MainSection>
            <CartPageTemplate.CartTotalSection>
                <Zone zoneName="Content1" contentId={id} />
            </CartPageTemplate.CartTotalSection>
        </CartPageTemplate>
    );
};

const widgetModule: WidgetModule = {
    component: OnePageCheckoutPageContainer,
    definition: {
        displayName: "Page Container",
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
    },
};

export default widgetModule;
